/* eslint-disable max-lines */
import { sessionStates, LMPSessionStatus } from '@constants/index';
import {
  plannerEventTypes,
  scheduleItemStatus,
  scheduleItemTypes
} from '@constants/schedule';
import { emptyObject } from '@constants/empty';
import { addTime, compareDates } from '@utils/dates';
import {
  getQuizState,
  classDidNotHappen,
  isCompletedWithNoVideoUrl,
  isCurrentlyLive
} from './Item';
import { getEntityData } from './User';

const getStatus = (item) => item.status;
const separator = ' • ';

const getClassIconColor = (classData) => {
  const { isLive, isMissed, topicGroup, isRecordingInProgress } = classData;
  if (isLive) return 'var(--color-i-red)';
  if (isRecordingInProgress) return 'var(--color-divider)';
  if (isMissed && topicGroup) return topicGroup.color; // for a class in test-series, there is no topic group
  return 'var(--color-i-black)';
};

const getClassInfo = (classData) => {
  const {
    rank,
    isLive,
    isUpcoming,
    isCancelled,
    isSpecial,
    isRecordingInProgress,
    isMissed,
    hasWatched,
    isOffline,
    offlineClassDetails,
    isPrimaryCentreBatch
  } = classData;

  if (isSpecial) return '';
  const prefix = `Session ${rank}`;
  if (isOffline && isPrimaryCentreBatch && (isLive || isUpcoming)) {
    const { centreDetails } = offlineClassDetails || {};
    const { roomDetails } = centreDetails || {};
    return roomDetails?.name
      ? `${roomDetails.name}${separator}${prefix}`
      : prefix;
  }
  if (isCancelled || classDidNotHappen(classData))
    return `${prefix}${separator}Cancelled`;
  if (isLive || isUpcoming || isCancelled) return prefix;
  if (isRecordingInProgress)
    return `${prefix}${separator}Recording in progress`;
  if (isMissed) return `${prefix}${separator}Missed`;
  if (hasWatched) return `${prefix}${separator}Completed`;
  return prefix;
};

const getTestIconColor = (itemData) => {
  const { isLive, isMissed, isOffline, isUpcoming } = itemData;
  if (isLive) return 'var(--color-i-red)';
  if (isOffline && isUpcoming) return 'var(--color-i-test)';
  if (isOffline && isMissed) return 'var(--color-i-black)';
  if (isMissed) return 'var(--color-i-test)';
  return 'var(--color-i-black)';
};

const getTestInfo = (itemData) => {
  const {
    isLive,
    isUpcoming,
    isCancelled,
    isMissed,
    duration,
    totalQuestions,
    isOffline
  } = itemData;
  if (isOffline && (isLive || isUpcoming || isMissed)) {
    const durationInMin = duration / 60;
    return `${durationInMin?.toFixed()} min`;
  }
  if (isLive || isUpcoming || isMissed) {
    const durationInMin = duration / 60;
    return `${durationInMin?.toFixed()} min${separator}${totalQuestions} questions`;
  }
  if (isCancelled) return 'Cancelled';
  return 'Attempted';
};

const getTestInfoV2 = (itemData) => {
  const { duration, totalQuestions } = itemData;
  const durationInMin = duration / 60;

  return `${durationInMin?.toFixed()} min${separator}${totalQuestions} questions`;
};

const getQuizIconColor = (itemData) => {
  const { isLive, isMissed } = itemData;
  if (isLive) return 'var(--color-i-red)';
  if (isMissed) return 'var(--color-i-quiz)';
  return 'var(--color-i-black)';
};

const getEventTypeForTest = (author, isVenueVisible) => {
  if (isVenueVisible) {
    return plannerEventTypes.OFFLINE_TEST;
  }
  return author ? plannerEventTypes.TEST_WITH_EDUCATOR : plannerEventTypes.TEST;
};

export const eventTypeMap = {
  [scheduleItemTypes.CLASS]: (classData) => {
    const { attachments, isUpcoming, isCancelled } = classData;
    const showPractice = attachments.quizzes.length > 0;
    const eventType = showPractice
      ? plannerEventTypes.CLASS_WITH_PRACTICE
      : plannerEventTypes.CLASS;
    return {
      eventType,
      iconColor: getClassIconColor(classData),
      showIcon: !isUpcoming && !isCancelled,
      info: getClassInfo(classData)
    };
  },
  [scheduleItemTypes.QUIZ]: (itemData) => {
    const { isUpcoming, isCancelled, info } = itemData;
    return {
      eventType: plannerEventTypes.QUIZ,
      showIcon: !isUpcoming && !isCancelled,
      iconColor: getQuizIconColor(itemData),
      info: info || getTestInfo(itemData)
    };
  },
  [scheduleItemTypes.TEST]: (itemData) => {
    const {
      author,
      isUpcoming,
      isCancelled,
      isOffline,
      offlineTestDetails,
      info
    } = itemData;
    const isVenueVisible = isOffline;
    return {
      eventType: getEventTypeForTest(author, isVenueVisible),
      showIcon: (!isUpcoming && !isCancelled) || isOffline,
      iconColor: getTestIconColor(itemData),
      info: info || getTestInfo(itemData),
      showVenue: isVenueVisible,
      venueName: offlineTestDetails?.venue
    };
  },
  [scheduleItemTypes.COMBAT]: (itemData) => {
    const { isUpcoming, isCancelled } = itemData;
    return {
      eventType: plannerEventTypes.COMBAT,
      showIcon: !isUpcoming && !isCancelled,
      info: getTestInfo(itemData)
    };
  },
  [scheduleItemTypes.LIVE_INTERACTION]: (finalItem) => {
    const durationInMin = finalItem.duration / 60;
    const info = `${durationInMin} min${durationInMin > 1 ? 's' : ''}`;
    return {
      info,
      eventType: plannerEventTypes.LIVE_INTERACTION
    };
  },
  [scheduleItemTypes.OPENHOUSE]: (finalItem) => {
    const durationInMin = finalItem.duration / 60;
    const info = `${durationInMin} min${
      durationInMin > 1 ? 's' : ''
    }${separator}Available on app`;
    return {
      info,
      eventType: plannerEventTypes.OPENHOUSE
    };
  },
  [scheduleItemTypes.DAILY_PRACTICE_REMINDER]: () => ({
    eventType: plannerEventTypes.DAILY_PRACTICE_REMINDER
  }),
  [scheduleItemTypes.OPENHOUSE]: () => ({
    eventType: plannerEventTypes.OPENHOUSE
  })
};
export const eventTypeMapV2 = {
  [scheduleItemTypes.CLASS]: (classData) => {
    const { attachments, isUpcoming, isCancelled } = classData;
    const showPractice = attachments.quizzes.length > 0;
    const eventType = showPractice
      ? plannerEventTypes.CLASS_WITH_PRACTICE
      : plannerEventTypes.CLASS;
    return {
      eventType,
      iconColor: getClassIconColor(classData),
      showIcon: !isUpcoming && !isCancelled,
      info: getClassInfo(classData)
    };
  },
  [scheduleItemTypes.QUIZ]: (itemData) => {
    const { isUpcoming, isCancelled } = itemData;
    return {
      eventType: plannerEventTypes.QUIZ,
      showIcon: !isUpcoming && !isCancelled,
      iconColor: getQuizIconColor(itemData),
      info: getTestInfoV2(itemData)
    };
  },
  [scheduleItemTypes.TEST]: (itemData) => {
    const { author, isUpcoming, isCancelled, isOffline, offlineTestDetails } =
      itemData;
    const isVenueVisible = isOffline;
    return {
      eventType: getEventTypeForTest(author, isVenueVisible),
      showIcon: (!isUpcoming && !isCancelled) || isOffline,
      iconColor: getTestIconColor(itemData),
      info: getTestInfoV2(itemData),
      showVenue: isVenueVisible,
      venueName: offlineTestDetails?.venue
    };
  },
  [scheduleItemTypes.COMBAT]: (itemData) => {
    const { isUpcoming, isCancelled } = itemData;
    return {
      eventType: plannerEventTypes.COMBAT,
      showIcon: !isUpcoming && !isCancelled,
      info: getTestInfo(itemData)
    };
  },
  [scheduleItemTypes.LIVE_INTERACTION]: (finalItem) => {
    const durationInMin = finalItem.duration / 60;
    const info = `${durationInMin} min${durationInMin > 1 ? 's' : ''}`;
    return {
      info,
      eventType: plannerEventTypes.LIVE_INTERACTION
    };
  },
  [scheduleItemTypes.OPENHOUSE]: (finalItem) => {
    const durationInMin = finalItem.duration / 60;
    const info = `${durationInMin} min${
      durationInMin > 1 ? 's' : ''
    }${separator}Available on app`;
    return {
      info,
      eventType: plannerEventTypes.OPENHOUSE
    };
  },
  [scheduleItemTypes.DAILY_PRACTICE_REMINDER]: () => ({
    eventType: plannerEventTypes.DAILY_PRACTICE_REMINDER
  }),
  [scheduleItemTypes.OPENHOUSE]: () => ({
    eventType: plannerEventTypes.OPENHOUSE
  })
};

const getSessionState = ({ liveAt, endsAt }) => {
  const currentDate = new Date();
  if (
    new Date(liveAt).getTime() <= currentDate.getTime() &&
    new Date(endsAt).getTime() >= currentDate.getTime()
  )
    return sessionStates.LIVE;

  const val = compareDates(liveAt, currentDate);

  if (val === -1) return sessionStates.FUTURE;
  if (val === 1) return sessionStates.PAST;
};

const getOpenhouseEducator = (openhouseDetails = {}) => {
  let educator = openhouseDetails.educator[0];
  educator = {
    firstName: educator.first_name,
    lastName: educator.last_name,
    name: `${educator.first_name} ${educator.last_name}`,
    username: educator.username,
    avatar: educator.avatar,
    uid: educator.uid,
    permalink: educator.permalink,
    isVerifiedEducator: educator.is_verified_educator
  };

  return educator;
};

const getOpenhouseItem = (itemData) => {
  const item = itemData.data;
  const type = itemData.object_type;
  const activityAt = itemData.activity_at;
  const activityAtDate = new Date(activityAt);

  const lessonDuration = itemData.duration;
  const endsAt = addTime(activityAtDate, lessonDuration, 's');
  const endsAtInMs = endsAt.getTime();
  const finalItem = {
    type,
    activityAt,
    liveAt: activityAt,
    status: itemData.status,
    educator: getOpenhouseEducator(item.openhouse_details),
    isActive: itemData.is_active,
    uid: itemData.object_uid,
    name: item.openhouse_details?.name || item.openhouse_details?.title,
    isOpenhouse: true,
    endsAt: endsAtInMs,
    finishedAt: new Date(itemData.finished_at || endsAtInMs).getTime(),
    isUpcoming: itemData.status === scheduleItemStatus.UPCOMING,
    hasWatched: itemData.status === scheduleItemStatus.WATCHED_OR_ATTEMPTED,
    isMissed: itemData.status === scheduleItemStatus.MISSED,
    isCancelled: itemData.status === scheduleItemStatus.CANCELLED,
    duration: lessonDuration
  };
  finalItem.state = getSessionState(finalItem);
  const isLive = finalItem.state === sessionStates.LIVE;
  const isUpcoming = !isLive && finalItem.isUpcoming;
  return {
    ...finalItem,
    isLive,
    isUpcoming,
    ...eventTypeMap[type](finalItem)
  };
};

const getLMPItem = (itemData) => {
  const item = itemData.data;
  const type = itemData.object_type;
  const { educator } = item;
  const activityAt = itemData.activity_at;
  const lessonDuration = itemData.duration;
  const liveAtDate = new Date(itemData.activity_at);
  const endsAt = addTime(liveAtDate, lessonDuration, 's');
  const endsAtInMs = endsAt.getTime();
  let educatorObj = null;
  if (educator) {
    educatorObj = {
      userName: educator.username,
      firstName: educator.first_name,
      lastName: educator.last_name,
      uid: educator.uid,
      avatar: educator.avatar,
      educatorTag: educator.educator_tag
    };
  }

  const finalItem = {
    type,
    status: itemData.status,
    isActive: itemData.is_active,
    uid: itemData.object_uid,
    name: item.vertical.parent.parent.title,
    thumbnail: item.vertical?.icon,
    liveAt: activityAt,
    activityAt,
    isLMP: true,
    isUpcoming: itemData.status === scheduleItemStatus.UPCOMING,
    hasWatched: itemData.status === scheduleItemStatus.WATCHED_OR_ATTEMPTED,
    isMissed: itemData.status === scheduleItemStatus.MISSED,
    isCancelled: itemData.status === scheduleItemStatus.CANCELLED,
    endsAt: endsAtInMs,
    finishedAt: new Date(endsAtInMs).getTime(),
    duration: lessonDuration,
    secondsBeforeLive: item.seconds_before_live,
    educator: educatorObj,
    subjectName: item.vertical.parent.title,
    lmpStatus: item.status,
    isRefunded: item.is_refunded,
    isWelcomeSession: item.is_welcome_session,
    isCompleted:
      item.status === LMPSessionStatus.STOPPED ||
      item.status === LMPSessionStatus.RECORDING_AVAILABLE
  };
  finalItem.state = getSessionState(finalItem);
  const isLive = finalItem.state === sessionStates.LIVE;
  const isUpcoming = !isLive && finalItem.isUpcoming;
  return {
    ...finalItem,
    isLive,
    isUpcoming,
    ...eventTypeMap[type](finalItem)
  };
};

const getLMPItemV2 = (itemData) => {
  const item = itemData.data;
  const type = itemData.object_type;
  const { educator } = item;
  const activityAt = itemData.activity_at;
  const lessonDuration = itemData.duration;
  const liveAtDate = new Date(itemData.activity_at);
  const endsAt = addTime(liveAtDate, lessonDuration, 's');
  const endsAtInMs = endsAt.getTime();
  let educatorObj = null;
  if (educator) {
    educatorObj = {
      userName: educator.username,
      firstName: educator.first_name,
      lastName: educator.last_name,
      uid: educator.uid,
      avatar: educator.avatar,
      educatorTag: educator.educator_tag
    };
  }

  const finalItem = {
    type,
    status: itemData.status,
    isActive: itemData.is_active,
    uid: itemData.object_uid,
    name: item.vertical.parent.parent.title,
    thumbnail: item.vertical?.icon,
    liveAt: activityAt,
    activityAt,
    isLMP: true,
    isUpcoming: item.status === LMPSessionStatus.UPCOMING,
    hasWatched: item.status === scheduleItemStatus.WATCHED_OR_ATTEMPTED,
    isMissed: item.status === scheduleItemStatus.MISSED,
    isCancelled: [
      LMPSessionStatus.CANCELLED,
      LMPSessionStatus.EDUCATOR_CANCELLED,
      LMPSessionStatus.MODERATOR_CANCELLED
    ].includes(item.status),
    endsAt: endsAtInMs,
    finishedAt: new Date(endsAtInMs).getTime(),
    duration: lessonDuration,
    secondsBeforeLive: item.seconds_before_live,
    educator: educatorObj,
    subjectName: item.vertical.parent.title,
    lmpStatus: item.status,
    isRefunded: item.is_refunded,
    isWelcomeSession: item.is_welcome_session,
    isCompleted:
      item.status === LMPSessionStatus.STOPPED ||
      item.status === LMPSessionStatus.RECORDING_AVAILABLE
  };
  finalItem.state = getSessionState(finalItem);
  const isLive = finalItem.state === sessionStates.LIVE;
  const isUpcoming = !isLive && finalItem.isUpcoming;
  return {
    ...finalItem,
    isLive,
    isUpcoming,
    ...eventTypeMapV2[type](finalItem)
  };
};

const getDailyPracticeItem = (itemData) => {
  const uid = itemData.object_uid;
  const name = 'Daily Practice';
  const item = itemData.data;
  const type = itemData.object_type;
  const activityAt = itemData.activity_at;
  const isActive = itemData.is_active;
  const totalCount = item.questions_count || 0;
  const questionsAttempted = item.questions_attempted || 0;
  const remainingCount = Math.max(totalCount - questionsAttempted, 0); // questionsAttempted can be more than totalCount
  const percentageCompleted = totalCount
    ? Math.min((questionsAttempted / totalCount) * 100, 100) // questionsAttempted can be more than totalCount
    : 0;
  const hasStarted = questionsAttempted > 0;
  const hasCompleted = remainingCount === 0;

  const liveAt = new Date(activityAt);
  const endsAt = addTime(liveAt, 10, 'm'); // live for 10 min

  const status = getStatus(itemData);
  const isUpcoming = status === scheduleItemStatus.UPCOMING;
  const isMissed = status === scheduleItemStatus.MISSED;

  return {
    uid,
    name,
    type,
    liveAt,
    endsAt,
    finishedAt: endsAt,
    activityAt,
    isActive,
    isMissed,
    isUpcoming,
    percentageCompleted,
    questionsAttempted,
    hasStarted,
    hasCompleted,
    totalCount,
    remainingCount,
    ...eventTypeMap[type](itemData)
  };
};

const getOfflineClassDetails = (itemData = {}) => {
  if (!itemData?.centre_details) return null;
  const { centre_details } = itemData;
  const updatedCentreDetails = {
    city: centre_details.city,
    name: centre_details.name,
    roomDetails: centre_details.room_details,
    status: centre_details.status,
    uid: centre_details.uid
  };
  return {
    offlineClassState: itemData.offline_class_state,
    centreDetails: updatedCentreDetails
  };
};

const getOfflineQuizDetails = (offlineQuizDetails = {}) => {
  const { venue, offline_quiz_state, mode } = offlineQuizDetails;
  if (!venue) return null;
  return {
    offlineQuizState: offline_quiz_state,
    venue,
    mode
  };
};

// Do not use dateKey, plannerItemType as props in the resultant object
export default function getNewScheduleItem(itemData) {
  const type = itemData.object_type;
  const isActive = itemData.is_active;
  const item = itemData.data;

  const isItemPost = type === scheduleItemTypes.CLASS;
  const isItemQuiz = type === scheduleItemTypes.QUIZ;
  const isTest = type === scheduleItemTypes.TEST;
  const isLMP = type === scheduleItemTypes.LIVE_INTERACTION;
  const isDailyPractice = type === scheduleItemTypes.DAILY_PRACTICE_REMINDER;
  const isOpenhouse = type === scheduleItemTypes.OPENHOUSE;

  if (isLMP) {
    return getLMPItem(itemData);
  }
  if (isDailyPractice) {
    return getDailyPracticeItem(itemData);
  }
  if (isOpenhouse) {
    return getOpenhouseItem(itemData);
  }

  const activityAt = itemData.activity_at;
  const startsAt = item.start_time || null;
  const liveAtDate = new Date(item.live_at || startsAt);
  const liveAt = liveAtDate.getTime();
  const activityAtDate = new Date(activityAt);

  const lessonDuration = itemData.duration;
  const endsAt = addTime(liveAtDate, lessonDuration, 's');
  const endsAtInMs = endsAt.getTime();
  const state = isItemPost
    ? item.state
    : getQuizState({ liveAtString: liveAt, endsAt, startsAt: activityAtDate });
  const status = getStatus(itemData);
  const programme = item.programme || emptyObject;
  const formattedItem = {
    type,
    isActive,
    rank: item.rank,
    uid: itemData.object_uid,
    name: item.name || item.title,
    slug: item.slug,
    permalink: item.permalink,
    relativeLink: item.relative_link,
    thumbnail: item.post_thumbnail || programme.cover_photo,
    liveAt,
    activityAt,
    author: item.author ? getEntityData(item.author) : null,
    liveClassUrl: item.live_class_url,
    videoUrl: item.video_url,
    topicGroup: item.topic_group,
    attachments: item.attachments,
    startsAt,
    hasWatched: status === scheduleItemStatus.WATCHED_OR_ATTEMPTED,
    isMissed: status === scheduleItemStatus.MISSED,
    isCancelled: status === scheduleItemStatus.CANCELLED,
    endsAt: endsAtInMs,
    finishedAt: new Date(itemData.finished_at || endsAtInMs).getTime(),
    secondsBeforeLive: item.seconds_before_live,
    state,
    status,
    isOpenhouse,
    isQuiz: isItemQuiz,
    isPost: isItemPost,
    duration: itemData.duration,
    totalQuestions: itemData?.data?.num_questions,
    totalScore: item?.total_score,
    isTest,
    programme,
    isSpecial: programme.is_special || false,
    isDormant: item.state === sessionStates.DORMANT,
    isWelcomeSession: item.is_welcome_session,
    isOffline: item.is_offline || false,
    isLiveStreamingEnabled: item.is_live_streaming_enabled ?? true,
    isPrimaryCentreBatch: item?.is_primary_centre_batch || false,
    offlineClassDetails:
      getOfflineClassDetails(item.offline_class_details) || null,
    offlineTestDetails: getOfflineQuizDetails(item.offline_quiz_details) || null
  };
  let isLive = formattedItem.state === sessionStates.LIVE;
  if (isItemQuiz) {
    isLive = state === sessionStates.LIVE;
  } else if (isItemPost) {
    isLive = isCurrentlyLive(formattedItem);
    // making isUpcoming false 15 min before the class starts
  }
  const isUpcoming = !isLive && status === scheduleItemStatus.UPCOMING;
  const finalItem = {
    ...formattedItem,
    isLive,
    isUpcoming,
    isCancelled: formattedItem.isCancelled,
    isRecordingInProgress: isCompletedWithNoVideoUrl(formattedItem)
  };
  return {
    ...finalItem,
    ...eventTypeMap[type](finalItem)
  };
}

export function getNewScheduleItemV2(itemData) {
  try {
    const type = itemData.object_type;
    const isActive = itemData.is_active;
    const item = itemData.data;

    const isItemPost = type === scheduleItemTypes.CLASS;
    const isItemQuiz = type === scheduleItemTypes.QUIZ;
    const isTest = type === scheduleItemTypes.TEST;
    const isLMP = type === scheduleItemTypes.LIVE_INTERACTION;

    if (isLMP) {
      return getLMPItemV2(itemData);
    }

    const activityAt = itemData.activity_at;
    const startsAt = item.start_time || null;
    const liveAtDate = new Date(item.live_at || startsAt);
    const liveAt = liveAtDate.getTime();
    const activityAtDate = new Date(activityAt);

    const lessonDuration = itemData.duration;
    const endsAt = addTime(liveAtDate, lessonDuration, 's');
    const endsAtInMs = endsAt.getTime();
    const state = isItemPost
      ? item.state
      : getQuizState({
          liveAtString: liveAt,
          endsAt,
          startsAt: activityAtDate
        });
    const status = getStatus(itemData);
    const programme = item.programme || emptyObject;
    const formattedItem = {
      type,
      isActive,
      rank: item.rank,
      uid: itemData.object_uid,
      name: item.name || item.title,
      slug: item.slug,
      permalink: item.permalink,
      relativeLink: item.relative_link,
      thumbnail: item.post_thumbnail || programme.cover_photo,
      liveAt,
      activityAt,
      author: item.author ? getEntityData(item.author) : null,
      liveClassUrl: item.live_class_url,
      videoUrl: item.video_url,
      topicGroup: item.topic_group,
      attachments: item.attachments,
      startsAt,
      isCancelled: status === scheduleItemStatus.CANCELLED,
      endsAt: endsAtInMs,
      finishedAt: new Date(itemData.finished_at || endsAtInMs).getTime(),
      state,
      status,
      isQuiz: isItemQuiz,
      isPost: isItemPost,
      duration: itemData.duration,
      totalQuestions: itemData?.data?.num_questions,
      totalScore: item?.total_score,
      isTest,
      programme,
      goalUid: item?.goal_uid,
      isSpecial: programme.is_special || false,
      isDormant: item.state === sessionStates.DORMANT,
      isWelcomeSession: item.is_welcome_session,
      isOffline: item.is_offline || false,
      isLiveStreamingEnabled: item.is_live_streaming_enabled ?? true,
      isPrimaryCentreBatch: item?.is_primary_centre_batch || false,
      offlineClassDetails:
        getOfflineClassDetails(item.offline_class_details) || null,
      offlineTestDetails:
        getOfflineQuizDetails(item.offline_quiz_details) || null
    };
    let isLive = formattedItem.state === sessionStates.LIVE;
    if (isItemQuiz) {
      isLive = state === sessionStates.LIVE;
    } else if (isItemPost) {
      isLive = isCurrentlyLive(formattedItem);
      // making isUpcoming false 15 min before the class starts
    }
    const isUpcoming = !isLive && status === scheduleItemStatus.UPCOMING;
    const finalItem = {
      ...formattedItem,
      isLive,
      isUpcoming,
      isCancelled: formattedItem.isCancelled,
      isRecordingInProgress: isCompletedWithNoVideoUrl(formattedItem)
    };
    return {
      ...finalItem,
      ...eventTypeMapV2[type](finalItem)
    };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Schedule Item Error', err);
    return {};
  }
}
